import React, { forwardRef, memo } from 'react';
import { CheckboxProps } from '@lib/components/CheckBox/CheckBox';
import classNames from 'classnames';
import Typography, { Variant } from '@lib/components/Typography/Typography';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import { CheckboxSizes } from '@lib/components/CheckBox/enums';
// NOTE: These styles are also imported by CheckBoxAsToggleCard
import styles from './CheckBoxAsToggle.module.scss';

type Props = {
  labelTypographyProps?: {
    variant?: Variant;
    strong?: boolean;
  };
};

const CheckBoxAsToggle = forwardRef(
  (props: CheckboxProps & Props, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      label = '',
      labelTypographyProps,
      checked,
      onChange,
      disabled,
      name,
      rightLabelIconTooltipProps,
      rightLabelIcon,
      size = CheckboxSizes.Large,
    } = props;
    const toggleWrapClassName = classNames({
      [styles.toggleGrid]: label,
      [styles.toggleWrap]: !label,
      [styles.toggleGridWithRightIcon]: rightLabelIcon,
      [styles.toggleLarge]: size === CheckboxSizes.Large,
      [styles.toggleSmall]: size === CheckboxSizes.Small,
    });
    const toggleRootClassName = classNames(styles.toggleRoot, {
      [styles.toggleRootLarge]: size === CheckboxSizes.Large,
      [styles.toggleRootSmall]: size === CheckboxSizes.Small,
      [styles.toggleRootChecked]: checked,
    });
    const labelTypographyVariant = labelTypographyProps?.variant || 'body';
    const labelTypographyStrong = labelTypographyProps?.strong ?? true;
    return (
      <>
        <input
          ref={ref}
          id={`${name}-toggle`}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className={styles.input}
        />
        <label htmlFor={`${name}-toggle`} className={toggleWrapClassName}>
          <div className={toggleRootClassName} />
          {!!label && (
            <Typography
              variant={labelTypographyVariant}
              strong={labelTypographyStrong}
            >
              {label}
            </Typography>
          )}
          {rightLabelIcon && !rightLabelIconTooltipProps && (
            <span className={styles.rightLabelIcon}>{rightLabelIcon}</span>
          )}
          {rightLabelIcon && rightLabelIconTooltipProps && (
            <span className={styles.rightLabelIcon}>
              <Tooltip maxWidth={160} {...rightLabelIconTooltipProps}>
                {rightLabelIcon}
              </Tooltip>
            </span>
          )}
        </label>
      </>
    );
  },
);

export default memo(CheckBoxAsToggle);
